import { max, min } from "date-fns";
import { useEffect, useState } from "react";
import { sendEvent } from "@/features/analytics/gtm/event";
import type { EventViewItemType } from "@/features/analytics/types/event.type";
import type { ConfigureDietFormType } from "@/features/cart/forms/configure-diet";
import type { DietCatalogDetail } from "@/features/diets/types";

export const useViewItemEvent = ({
  item,
  configuration,
  price,
  end,
  loading,
}: {
  item: DietCatalogDetail;
  configuration: Pick<ConfigureDietFormType, "dates" | "kcal">;
  price?: number;
  end?: boolean;
  loading?: boolean;
}) => {
  const [ecommerce, setEcommerce] = useState<
    EventViewItemType["ecommerce"] | null
  >(null);
  const [isCustomizeDietEndSent, setIsCustomizeDietEndSent] =
    useState<boolean>(false);
  const [datesHolder, setDatesHolder] = useState<Date[]>(
    configuration.dates || [],
  );
  const configurationTimes = configuration.dates?.map((date) => date.getTime());
  const holderTimes = datesHolder?.map((date) => date.getTime());

  const firstDate = min(configuration.dates || []);
  const lastDate = max(configuration.dates || []);

  // compare if the dates are the same
  useEffect(() => {
    const intersection = configurationTimes
      ?.filter((time) => !holderTimes?.includes(time))
      .concat(
        holderTimes?.filter((date) => !configurationTimes?.includes(date)),
      );
    if (intersection?.length > 0) {
      setDatesHolder(configuration.dates);
    }
  }, [configuration.dates]);

  useEffect(() => {
    const days = configuration.dates?.length || 1;
    const dailyPrice = price && days > 0 ? price / days : 0;

    const ecommerceData = {
      currency: "PLN",
      value: price || 0,
      items: [
        {
          item_id: item.id,
          item_name: item.name,
          item_brand: item.catering.name,
          item_category: item.tags.map((tag) => tag.slug).join(", "),
          item_category2: configuration.kcal || String(item.base.caloricity),
          item_category3: firstDate.getTime(),
          item_category4: lastDate.getTime(),
          item_variant: item.menuSelection ? "diet with choice" : "diet",
          price: dailyPrice,
          duration: configuration.dates?.length || 0,
          quantity: 1,
        },
      ],
    };
    setEcommerce(ecommerceData);

    if (!loading && price && price > 0) {
      if (end) {
        sendCustomizeDietEndEvent(ecommerceData);
      }
      sendViewItemEvent(ecommerceData);
    }
  }, [price]);

  const sendViewItemEvent = (
    ecommerceData?: EventViewItemType["ecommerce"] | null,
  ) => {
    const innerData = ecommerceData || ecommerce;
    if (innerData) {
      sendEvent({
        event: "view_item",
        ecommerce: innerData,
      });
    }
  };

  const sendCustomizeDietEndEvent = (
    ecommerceData?: EventViewItemType["ecommerce"],
  ) => {
    const innerData = ecommerceData || ecommerce;
    if (innerData) {
      setIsCustomizeDietEndSent(true);
      sendEvent({
        event: "CUSTOMIZE_DIET_END",
        ecommerce: innerData,
      });
    }
  };

  return {
    sendCustomizeDietEndEvent,
    sendViewItemEvent,
    isCustomizeDietEndSent,
  };
};
